import { configureStore } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { combineReducers } from 'redux';
import authReducer from '../features/auth/authSlice';
import tenantReducer from '../features/tenants/tenantSlice';
import userReducer from '../features/users/userSlice';
import settingsReducer from '../features/settings/settingsSlice';

// Persist configuration.
//
// auth gets its OWN nested config so the transient request flags are never
// persisted: whitelisting the whole slice froze `isLoading: true` from a
// login attempt interrupted mid-flight, and every later page load rehydrated
// the stuck flag — the Sign In button spun forever and the form was dead
// until localStorage was cleared by hand. Only durable identity fields
// survive a reload; isLoading/error always start fresh. (Moving auth out of
// the root whitelist also self-heals browsers that already carry a stuck
// blob under persist:admin-portal — it is simply no longer read.)
const authPersistConfig = {
  key: 'auth',
  storage,
  blacklist: ['isLoading', 'error'],
};

const persistConfig = {
  key: 'admin-portal',
  storage,
  whitelist: ['settings'], // auth persists via its own nested config above
};

// Combine reducers
const rootReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authReducer),
  tenant: tenantReducer,
  user: userReducer,
  settings: settingsReducer,
});

// Create persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;